// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Review of a ready Set_Proposal (blueprint-sets Req 8.5, 8.6, 8.8, 9.4, 10.1-10.9; design "SetProposalReview").
// Every edit goes through `reviewReducer` (proposalState.ts). The state is mirrored to sessionStorage under
// `setProposal:<runId>` on every change and on `bda:session-expired`, so a reload or a sign-in round trip keeps it
// (Req 10.9). Nothing here calls an endpoint that creates anything except "Create set" (and, from task 15.5, the
// "Preview set" slot), Req 10.4.
import { useEffect, useMemo, useReducer, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import Container from '@cloudscape-design/components/container';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Multiselect, { type MultiselectProps } from '@cloudscape-design/components/multiselect';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import {
  BLUEPRINT_NAME_PATTERN,
  MAX_DESCRIPTION,
  NAME_MESSAGE,
  pageRanges,
  validateFields,
  validateForm,
  type SetProposal,
  type ValidationErrors,
  type setProposalStatusResponse,
} from '@bda/shared';
import type { z } from 'zod';

import { api, ApiError } from '../../api/client';
import { SESSION_EXPIRED_EVENT } from '../../auth/auth';
import { runtimeConfig } from '../../config';
import { errorMessage } from '../../lib/notify';
import {
  createBlockers,
  includedFields,
  initialReview,
  reviewReducer,
  toCreateRequest,
  type ReviewAction,
  type ReviewState,
  type ReviewType,
} from './proposalState';
import {
  initialState as editorInitialState,
  reducer as editorReducer,
  type Action as EditorAction,
} from '../blueprints/editorState';
import { FieldsEditor } from '../blueprints/FieldsEditor';
import { PacketDocument, PageThumbnails } from '../../components/PageThumbnails';

export type ExistingMatch = NonNullable<z.infer<typeof setProposalStatusResponse>['existingMatches']>[number];

// ---------- sessionStorage mirror (Req 10.9) ----------

export const reviewStorageKey = (runId: string): string => `setProposal:${runId}`;

export function saveReview(state: ReviewState): void {
  try {
    window.sessionStorage.setItem(reviewStorageKey(state.runId), JSON.stringify({ state, at: Date.now() }));
  } catch {
    // Storage full or unavailable: the review still works, it just is not kept across a reload.
  }
}

const isArr = Array.isArray;

/** The stored review of this run, or null when there is none or it is not a review of this run. */
export function loadReview(runId: string): ReviewState | null {
  const raw = window.sessionStorage.getItem(reviewStorageKey(runId));
  if (!raw) return null;
  try {
    const s = (JSON.parse(raw) as { state?: Partial<ReviewState> }).state;
    if (
      !s ||
      s.runId !== runId ||
      typeof s.name !== 'string' ||
      typeof s.description !== 'string' ||
      typeof s.pages !== 'number' ||
      !isArr(s.types) ||
      !isArr(s.unassigned) ||
      !isArr(s.unclassified) ||
      !isArr(s.modelFlags) ||
      !isArr(s.mergeFlags)
    )
      return null;
    return s as ReviewState;
  } catch {
    return null;
  }
}

export const clearReview = (runId: string): void => window.sessionStorage.removeItem(reviewStorageKey(runId));

/** A default set name from the packet's file name: its base name with characters a name cannot have dropped. */
export function defaultSetName(fileName: string): string {
  const base = fileName
    .replace(/\.[^.]*$/, '')
    .replace(/[^A-Za-z0-9 _-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 128)
    .trim();
  return BLUEPRINT_NAME_PATTERN.test(base) ? base : '';
}

// ---------- helpers ----------

const label = (t: ReviewType): string => t.name.trim() || t.key;

export const formatPages = (pages: number[]): string =>
  pageRanges(pages)
    .map((r) => (r.start === r.end ? `${r.start}` : `${r.start}-${r.end}`))
    .join(', ');

/**
 * `validateFields` of the included fields, with the keys mapped back to the indices of `t.fields`, so FieldsEditor
 * (which shows every field, included or not) shows each message next to its field (Req 10.3).
 */
export function fieldErrorsOf(t: ReviewType): ValidationErrors {
  const indices = t.fields.map((_, i) => i).filter((i) => !t.excludedFields.includes(i));
  const out: ValidationErrors = {};
  for (const [k, v] of Object.entries(
    validateFields(includedFields(t), { documentType: t.documentType, description: t.description }),
  )) {
    const m = /^fields\.(\d+)(.*)$/.exec(k);
    const at = m ? indices[Number(m[1])] : undefined;
    out[m && at !== undefined ? `fields.${at}${m[2]}` : k] = v;
  }
  return out;
}

/** Messages of a failed "Create set" (Req 10.7): per type key, and the rest for the set as a whole. */
function createFailure(
  e: unknown,
  state: ReviewState,
): { message: string; typeErrors: Record<string, string>; other: string[] } {
  const message = errorMessage(e);
  if (!(e instanceof ApiError) || !e.details) return { message, typeErrors: {}, other: [] };
  const typeErrors: Record<string, string> = {};
  const raw = e.details.typeErrors;
  if (raw && typeof raw === 'object') {
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) if (typeof v === 'string') typeErrors[k] = v;
  }
  const other: string[] = [];
  const fieldErrors = e.details.fieldErrors;
  if (fieldErrors && typeof fieldErrors === 'object') {
    for (const [k, v] of Object.entries(fieldErrors as Record<string, unknown>)) {
      if (typeof v !== 'string' || /^types\.\d+(\.|$)/.test(k)) continue;
      other.push(k === 'name' ? `Set name: ${v}` : k === 'description' ? `Set description: ${v}` : v);
    }
  }
  // A type error for a key no longer in the review still has to be visible.
  const keys = new Set(state.types.map((t) => t.key));
  for (const [k, v] of Object.entries(typeErrors)) if (!keys.has(k)) other.push(v);
  return { message, typeErrors, other };
}

// ---------- pieces ----------

const UNASSIGNED = 'unassigned';

function MovePages({
  pages,
  targets,
  onMove,
}: {
  pages: number[];
  targets: SelectProps.Option[];
  onMove: (pages: number[], to: string) => void;
}) {
  const [selected, setSelected] = useState<readonly MultiselectProps.Option[]>([]);
  const [to, setTo] = useState<SelectProps.Option | null>(null);
  const options = pages.map((p) => ({ label: `Page ${p}`, value: String(p) }));
  // Pages that moved away meanwhile drop out of the selection; a target that is gone clears.
  const chosen = selected.filter((o) => pages.includes(Number(o.value)));
  const target = to && targets.some((t) => t.value === to.value) ? to : null;
  if (pages.length === 0 || targets.length === 0) return null;
  return (
    <SpaceBetween direction="horizontal" size="xs" alignItems="end">
      <FormField label="Move pages">
        <Multiselect
          selectedOptions={chosen}
          options={options}
          placeholder="Choose pages"
          onChange={({ detail }) => setSelected(detail.selectedOptions)}
        />
      </FormField>
      <FormField label="To">
        <Select
          selectedOption={target}
          options={targets}
          placeholder="Choose where"
          onChange={({ detail }) => setTo(detail.selectedOption)}
        />
      </FormField>
      <Button
        disabled={chosen.length === 0 || !target}
        onClick={() => {
          onMove(
            chosen.map((o) => Number(o.value)),
            target!.value!,
          );
          setSelected([]);
        }}
      >
        Move
      </Button>
    </SpaceBetween>
  );
}

function TypeSection({
  t,
  state,
  dispatch,
  matches,
  blockers,
  serverError,
}: {
  t: ReviewType;
  state: ReviewState;
  dispatch: (a: ReviewAction) => void;
  matches: ExistingMatch[];
  blockers: string[];
  serverError: string | undefined;
}) {
  const others = state.types.filter((o) => o.key !== t.key);
  const flags = state.mergeFlags.filter((f) => f.a === t.key || f.b === t.key);
  const metaErrors =
    t.use.kind === 'new'
      ? validateForm({ name: t.name, documentType: t.documentType, description: t.description, fields: [] })
      : {};
  const fieldErrors = useMemo(() => fieldErrorsOf(t), [t]);
  const fieldsDispatch = (a: EditorAction) => {
    const next = editorReducer(
      editorInitialState({ name: t.name, documentType: t.documentType, description: t.description, fields: t.fields }),
      a,
    );
    dispatch({ type: 'setFields', key: t.key, fields: next.form.fields });
  };
  const targets: SelectProps.Option[] = [
    ...others.map((o) => ({ label: label(o), value: o.key })),
    { label: 'Unassigned pages', value: UNASSIGNED },
  ];
  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText={label(t)}
      headerDescription={`${t.documentType || 'No document type'} · ${t.pages.length === 0 ? 'No pages' : `Pages ${formatPages(t.pages)}`}${t.included ? '' : ' · Not included'}`}
      headerActions={
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Checkbox
            checked={t.included}
            onChange={({ detail }) => dispatch({ type: 'include', key: t.key, included: detail.checked })}
          >
            Include in set
          </Checkbox>
          <Button onClick={() => dispatch({ type: 'remove', key: t.key })} ariaLabel={`Remove ${label(t)}`}>
            Remove
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="m">
        {serverError && (
          <Alert type="error" header="Could not create this blueprint">
            {serverError}
          </Alert>
        )}
        {t.included && blockers.length > 0 && (
          <Alert type="warning">
            {blockers.length === 1 ? (
              blockers[0]
            ) : (
              <ul>
                {blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        {flags.map((f) => {
          const other = state.types.find((o) => o.key === (f.a === t.key ? f.b : f.a));
          if (!other) return null;
          return (
            <Alert
              key={`${f.a}|${f.b}`}
              type="warning"
              header={`Possibly the same type as ${label(other)}`}
              action={
                <Button onClick={() => dispatch({ type: 'merge', keep: other.key, remove: t.key })}>
                  Merge into {label(other)}
                </Button>
              }
            >
              {f.reason}
            </Alert>
          );
        })}
        {t.error && <Alert type="info">Fields could not be proposed for this type: {t.error}</Alert>}
        {t.droppedFields > 0 && (
          <Box color="text-body-secondary">
            {t.droppedFields === 1
              ? '1 proposed field was dropped because it was not valid.'
              : `${t.droppedFields} proposed fields were dropped because they were not valid.`}
          </Box>
        )}
        {t.use.kind === 'existing' ? (
          <Alert
            type="success"
            action={
              <Button onClick={() => dispatch({ type: 'useNew', key: t.key })}>Create a new blueprint instead</Button>
            }
          >
            Uses your blueprint {t.use.name} ({t.use.ref === 'DRAFT' ? 'draft' : `version ${t.use.ref}`}).
          </Alert>
        ) : (
          matches.map((m) => (
            <Alert
              key={m.blueprintId}
              type="info"
              header="You already have a blueprint for this document type"
              action={
                <Button
                  onClick={() =>
                    dispatch({
                      type: 'useExisting',
                      key: t.key,
                      blueprintId: m.blueprintId,
                      name: m.name,
                      ref: m.latestVersion ?? 'DRAFT',
                    })
                  }
                >
                  Use existing blueprint
                </Button>
              }
            >
              {m.name} ({m.documentType}, {m.latestVersion === null ? 'draft only' : `version ${m.latestVersion}`})
            </Alert>
          ))
        )}
        <FormField label="Name" errorText={metaErrors.name}>
          <Input
            value={t.name}
            onChange={({ detail }) => dispatch({ type: 'editType', key: t.key, name: detail.value })}
          />
        </FormField>
        <FormField label="Document type" errorText={metaErrors.documentType}>
          <Input
            value={t.documentType}
            onChange={({ detail }) => dispatch({ type: 'editType', key: t.key, documentType: detail.value })}
          />
        </FormField>
        <FormField
          label="Description"
          description="What the document is and how it differs from the other types. BDA matches pages to blueprints by it."
          errorText={metaErrors.description}
        >
          <Textarea
            value={t.description}
            onChange={({ detail }) => dispatch({ type: 'editType', key: t.key, description: detail.value })}
          />
        </FormField>
        <FormField
          label="Pages"
          description={t.pages.length === 0 ? 'No pages are in this type.' : `Pages ${formatPages(t.pages)}`}
        >
          <SpaceBetween size="s">
            <PageThumbnails pages={t.pages} />
            <MovePages
              pages={t.pages}
              targets={targets}
              onMove={(pages, to) => dispatch({ type: 'movePages', pages, to })}
            />
          </SpaceBetween>
        </FormField>
        {t.use.kind === 'new' && (
          <SpaceBetween size="s">
            <Header variant="h3" description="Clear a field to leave it out of the blueprint.">
              Fields
            </Header>
            {t.fields.length > 0 && (
              <SpaceBetween direction="horizontal" size="m">
                {t.fields.map((f, i) => (
                  <Checkbox
                    key={i}
                    checked={!t.excludedFields.includes(i)}
                    onChange={() => dispatch({ type: 'toggleField', key: t.key, index: i })}
                  >
                    Include {f.name || `field ${i + 1}`}
                  </Checkbox>
                ))}
              </SpaceBetween>
            )}
            <FieldsEditor fields={t.fields} errors={fieldErrors} dispatch={fieldsDispatch} />
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
}

function LeftOverPages({
  title,
  description,
  items,
  targets,
  dispatch,
}: {
  title: string;
  description: string;
  items: Array<{ page: number; reason: string }>;
  targets: SelectProps.Option[];
  dispatch: (a: ReviewAction) => void;
}) {
  if (items.length === 0) return null;
  return (
    <Container
      header={
        <Header variant="h2" counter={`(${items.length})`} description={description}>
          {title}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {items.map((u) => (
          <SpaceBetween key={u.page} direction="horizontal" size="m" alignItems="center">
            <PageThumbnails pages={[u.page]} />
            <Box>{u.reason}</Box>
          </SpaceBetween>
        ))}
        <MovePages
          pages={items.map((u) => u.page)}
          targets={targets}
          onMove={(pages, to) => dispatch({ type: 'movePages', pages, to })}
        />
      </SpaceBetween>
    </Container>
  );
}

// ---------- the review ----------

export interface SetProposalReviewProps {
  proposal: SetProposal;
  existingMatches: ExistingMatch[];
  /** The Sample_Packet for thumbnails; null when there is no document URL (no thumbnails then). */
  document: { url: string; contentType: string; name: string } | null;
  /** Set name the review starts with when nothing is stored. */
  defaultName: string;
  /** Defaults to the deployment's `sets.maxSetMembers`. */
  maxMembers?: number;
  /**
   * Slot for "Preview set" (task 15.5): `action` renders next to "Create set", `panel` above the actions. Both get
   * the current review state (for `toPreviewRequest` and its basis key).
   */
  preview?: { action?: (state: ReviewState) => ReactNode; panel?: (state: ReviewState) => ReactNode };
}

export function SetProposalReview({
  proposal,
  existingMatches,
  document,
  defaultName,
  maxMembers,
  preview,
}: SetProposalReviewProps) {
  const runId = proposal.runId;
  const navigate = useNavigate();
  const [restored] = useState(() => loadReview(runId));
  const [state, dispatch] = useReducer(reviewReducer, null, () => restored ?? initialReview(proposal, defaultName));
  const [creating, setCreating] = useState(false);
  const [failure, setFailure] = useState<ReturnType<typeof createFailure> | null>(null);

  // Mirror every change, and once more just before leaving for sign-in (Req 10.9).
  useEffect(() => {
    saveReview(state);
    const onExpired = () => saveReview(state);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [state]);

  const max = maxMembers ?? runtimeConfig().sets.maxSetMembers;
  const blockers = useMemo(() => createBlockers(state, max), [state, max]);
  const byType = (key: string) => blockers.filter((b) => b.key === key).map((b) => b.message);
  const nameError = !BLUEPRINT_NAME_PATTERN.test(state.name) || state.name.trim() === '' ? NAME_MESSAGE : undefined;
  const descriptionError =
    state.description.length > MAX_DESCRIPTION ? `Keep the description to ${MAX_DESCRIPTION} characters.` : undefined;
  const typeTargets: SelectProps.Option[] = state.types.map((t) => ({ label: label(t), value: t.key }));

  const create = async () => {
    setCreating(true);
    setFailure(null);
    try {
      const res = await api.createSetFromProposal(runId, toCreateRequest(state));
      clearReview(runId);
      navigate(`/sets/${encodeURIComponent(res.set.id)}`);
    } catch (e) {
      // The review stays as it is so the author can fix it and retry (Req 10.7).
      setFailure(createFailure(e, state));
    } finally {
      setCreating(false);
    }
  };

  const body = (
    <SpaceBetween size="l">
      {restored && <Alert type="info">Your earlier edits to this proposal were restored.</Alert>}
      <Container header={<Header variant="h2">Set</Header>}>
        <SpaceBetween size="m">
          <FormField label="Set name" errorText={nameError}>
            <Input value={state.name} onChange={({ detail }) => dispatch({ type: 'setMeta', name: detail.value })} />
          </FormField>
          <FormField label="Set description" description="Optional." errorText={descriptionError}>
            <Textarea
              value={state.description}
              onChange={({ detail }) => dispatch({ type: 'setMeta', description: detail.value })}
            />
          </FormField>
          <Box color="text-body-secondary">
            {state.pages} pages in the sample packet. Nothing is created until you choose "Create set".
          </Box>
        </SpaceBetween>
      </Container>
      <Header
        variant="h2"
        counter={`(${state.types.length})`}
        description="One blueprint per document type. Review and edit each before you create the set."
      >
        Document types
      </Header>
      {state.types.length === 0 && <Box>No document types are left in this proposal.</Box>}
      {state.types.map((t) => (
        <TypeSection
          key={t.key}
          t={t}
          state={state}
          dispatch={dispatch}
          matches={existingMatches.filter((m) => m.key === t.key)}
          blockers={byType(t.key)}
          serverError={failure?.typeErrors[t.key]}
        />
      ))}
      <LeftOverPages
        title="Unassigned pages"
        description="Pages in no document type, for example blank pages or cover sheets. Move a page to a type to include it."
        items={state.unassigned}
        targets={typeTargets}
        dispatch={dispatch}
      />
      <LeftOverPages
        title="Unclassified pages"
        description="Pages that could not be classified. The other document types are still proposed."
        items={state.unclassified}
        targets={[...typeTargets, { label: 'Unassigned pages', value: UNASSIGNED }]}
        dispatch={dispatch}
      />
      {preview?.panel?.(state)}
    </SpaceBetween>
  );

  return (
    <Form
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          {preview?.action?.(state)}
          <Button
            variant="primary"
            disabled={blockers.length > 0 || creating}
            loading={creating}
            onClick={() => void create()}
          >
            Create set
          </Button>
        </SpaceBetween>
      }
      secondaryActions={
        blockers.length > 0 ? (
          <Box color="text-status-warning" variant="small">
            Create set is unavailable: {blockers.length === 1 ? 'fix 1 problem.' : `fix ${blockers.length} problems.`}
          </Box>
        ) : undefined
      }
      errorText={
        failure ? (
          <SpaceBetween size="xxs">
            <span>{failure.message}</span>
            {failure.other.map((m) => (
              <span key={m}>{m}</span>
            ))}
          </SpaceBetween>
        ) : undefined
      }
    >
      <SpaceBetween size="l">
        {document ? (
          <PacketDocument url={document.url} contentType={document.contentType} name={document.name}>
            {body}
          </PacketDocument>
        ) : (
          body
        )}
        {blockers.length > 0 && (
          <Alert type="warning" header="Create set is unavailable">
            <ul>
              {blockers.map((b, i) => (
                <li key={i}>{b.message}</li>
              ))}
            </ul>
          </Alert>
        )}
      </SpaceBetween>
    </Form>
  );
}
